/**
 * ContentStudioHomePage -- Screen A
 * Route: /content-studio
 * Step 9F Green Phase -- Xyden Leadership Assessment Platform
 *
 * Version-scoped dashboard showing content stats for the selected version
 * and quick-action navigation cards linking to each Content Studio page.
 */

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageSpinner } from "../../components/PageSpinner";
import { useVersionStore } from "../../stores/versionStore";
import { apiClient } from "../../services/apiClient";
import { Card, CardContent } from "../../components/ui/card";
import { BookOpen, HelpCircle, Grid3X3, Gauge, GitBranch, ShieldCheck, Check, X } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { Version } from "../../types";

// ---------------------------------------------------------------------------
// Types for API responses (only fields we actually read)
// ---------------------------------------------------------------------------

interface Competency {
  id: string;
  version_id: string;
}

interface Question {
  id: string;
  status: string;
  version_id: string;
}

interface TriadMapping {
  id: string;
  faculty_id: string;
  amp_behaviour_id: string;
  amp_technique_id: string;
  com_behaviour_id: string;
  com_technique_id: string;
  version_id: string;
}

interface ScoringRule {
  id: string;
}

interface MaturityMap {
  id: string;
  rules?: unknown[];
}

interface NarrativeCoverage {
  total: number;
  generated: number;
  missing: number;
}

interface ValidationCheck {
  name: string;
  passed: boolean;
}

interface ValidationResult {
  version_id: string;
  status: string;
  checks: ValidationCheck[];
  blocking_errors: Array<{ condition: string; severity: string }>;
  warnings: Array<{ condition: string }>;
}

// ---------------------------------------------------------------------------
// Dashboard stats derived from API data
// ---------------------------------------------------------------------------

interface DashboardStats {
  faculties: number;
  behaviours: number;
  techniques: number;
  questions: number;
  questionsValid: number;
  questionsIncomplete: number;
  triads: number;
  triadsComplete: number;
  triadsGaps: number;
  scoringRules: number;
  maturityMaps: number;
  narrativesGenerated: number;
  narrativesMissing: number;
  versions: Version[];
}

const INITIAL_STATS: DashboardStats = {
  faculties: 0,
  behaviours: 0,
  techniques: 0,
  questions: 0,
  questionsValid: 0,
  questionsIncomplete: 0,
  triads: 0,
  triadsComplete: 0,
  triadsGaps: 0,
  scoringRules: 0,
  maturityMaps: 0,
  narrativesGenerated: 0,
  narrativesMissing: 0,
  versions: [],
};

// ---------------------------------------------------------------------------
// Quick Action Card
// ---------------------------------------------------------------------------

function QuickActionCard({
  icon: Icon,
  iconColor,
  title,
  description,
  badges,
  onClick,
}: {
  icon: LucideIcon;
  iconColor: string;
  title: string;
  description: string;
  badges: Array<{ label: string; value: number; color?: string }>;
  onClick: () => void;
}) {
  return (
    <Card
      className="border-brand-steel/50 bg-white shadow-sm cursor-pointer hover:shadow-md hover:border-brand-steel transition-all"
      onClick={onClick}
      role="link"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter") onClick();
      }}
    >
      <CardContent className="p-5 py-1">
        <div className="flex items-center gap-2.5 mb-3">
          <div className={`flex items-center justify-center w-9 h-9 rounded-lg ${iconColor}`}>
            <Icon size={18} className="text-white" aria-hidden="true" />
          </div>
          <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
        </div>
        <p className="text-sm text-brand-steel mb-4 line-clamp-2">{description}</p>
        <div className="flex flex-wrap gap-2">
          {badges.map((badge) => (
            <span
              key={badge.label}
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-xs font-medium ${
                badge.color ?? "bg-brand-panel text-gray-700"
              }`}
            >
              <span className="font-semibold">{badge.value}</span> {badge.label}
            </span>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Helper: determine if a triad mapping is complete (all 5 competency slots filled)
// ---------------------------------------------------------------------------

function isTriadComplete(t: TriadMapping): boolean {
  return !!(t.faculty_id && t.amp_behaviour_id && t.amp_technique_id && t.com_behaviour_id && t.com_technique_id);
}

// ---------------------------------------------------------------------------
// Main Page Component
// ---------------------------------------------------------------------------

export function ContentStudioHomePage() {
  const selectedVersion = useVersionStore((s) => s.selectedVersion);
  const [stats, setStats] = useState<DashboardStats>(INITIAL_STATS);
  const [validation, setValidation] = useState<ValidationResult | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  const versionId = selectedVersion?.id;

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);

    const vParam = versionId ? `?version_id=${versionId}` : "";

    const validationUrl = versionId ? `/api/validation/${versionId}` : null;

    // /api/narratives/coverage requires version_id (Query(...) on the
    // backend). The other entity tiles default to "all versions" when
    // version_id is omitted, but coverage 422s. Skip the call entirely
    // when no version is selected — same pattern as validationUrl above.
    const coverageReq = versionId
      ? apiClient.get<NarrativeCoverage>(`/api/narratives/coverage${vParam}`)
      : Promise.resolve(null);

    Promise.all([
      apiClient.get<Competency[]>(`/api/faculties${vParam}`),
      apiClient.get<Competency[]>(`/api/behaviours${vParam}`),
      apiClient.get<Competency[]>(`/api/techniques${vParam}`),
      apiClient.get<Question[]>(`/api/questions${vParam}`),
      apiClient.get<TriadMapping[]>(`/api/triad-mappings${vParam}`),
      apiClient.get<ScoringRule[]>(`/api/scoring-rules${vParam}`),
      apiClient.get<MaturityMap[]>(`/api/maturity-mappings${vParam}`),
      coverageReq,
      apiClient.get<{ data: Version[]; pagination: unknown }>("/api/versions"),
      validationUrl ? apiClient.get<ValidationResult>(validationUrl) : Promise.resolve(null),
    ])
      .then(
        ([faculties, behaviours, techniques, questions, triads, rules, maps, coverage, versionsRes, validationData]) => {
          if (cancelled) return;
          setValidation(
            validationData
              ? {
                  ...validationData,
                  checks: validationData.checks ?? [],
                  blocking_errors: validationData.blocking_errors ?? [],
                  warnings: validationData.warnings ?? [],
                }
              : null
          );
          const complete = triads.filter(isTriadComplete).length;
          setStats({
            faculties: faculties.length,
            behaviours: behaviours.length,
            techniques: techniques.length,
            questions: questions.length,
            questionsValid: questions.filter((q) => q.status === "Valid" || q.status === "Active").length,
            questionsIncomplete: questions.filter((q) => q.status !== "Valid" && q.status !== "Active").length,
            triads: triads.length,
            triadsComplete: complete,
            triadsGaps: triads.length - complete,
            scoringRules: rules.length,
            maturityMaps: maps.length,
            // coverage is null when no version is selected (skipped to
            // avoid the 422 from /narratives/coverage's required version_id).
            narrativesGenerated: coverage?.generated ?? 0,
            narrativesMissing: coverage?.missing ?? 0,
            versions: versionsRes.data,
          });
        }
      )
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [versionId]);

  if (isLoading) return <PageSpinner label="Loading dashboard" />;

  const activeCount = stats.versions.filter((v) => v.state === "Active").length;
  const draftCount = stats.versions.filter((v) => v.state === "Draft").length;
  const archivedCount = stats.versions.filter((v) => v.state === "Archived").length;

  return (
    <main className="p-6 w-full">
      <h1 className="text-lg font-semibold text-gray-900 mb-6">Quick Actions</h1>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <QuickActionCard
          icon={Grid3X3}
          iconColor="bg-amber-500"
          title="Triad Mapping Matrix"
          description="View and manage the matrix linking Activities to Faculties, Behaviors, and Techniques."
          badges={[
            { label: "Complete", value: stats.triadsComplete, color: "bg-green-50 text-green-700" },
            { label: "Gaps", value: stats.triadsGaps, color: "bg-red-50 text-red-600" },
          ]}
          onClick={() => navigate("/content-studio/triad-matrix")}
        />
        <QuickActionCard
          icon={BookOpen}
          iconColor="bg-blue-500"
          title="Competency Library"
          description="Manage Faculties, Behaviors, and Techniques with definitions and question assignments."
          badges={[
            { label: "Faculties", value: stats.faculties },
            { label: "Behaviors", value: stats.behaviours },
            { label: "Techniques", value: stats.techniques },
          ]}
          onClick={() => navigate("/content-studio/competencies")}
        />
        <QuickActionCard
          icon={HelpCircle}
          iconColor="bg-purple-500"
          title="Question Bank"
          description="Browse and manage all assessment questions across competency types and variants."
          badges={[
            { label: "Questions", value: stats.questions },
            { label: "Valid", value: stats.questionsValid, color: "bg-green-50 text-green-700" },
            { label: "Incomplete", value: stats.questionsIncomplete, color: "bg-red-50 text-red-600" },
          ]}
          onClick={() => navigate("/content-studio/questions")}
        />
        <QuickActionCard
          icon={Gauge}
          iconColor="bg-teal-500"
          title="Scoring Rules"
          description="Configure competency scoring rule sets and activity maturity mapping tables."
          badges={[
            { label: "Rules", value: stats.scoringRules },
            { label: "Maturity Maps", value: stats.maturityMaps },
          ]}
          onClick={() => navigate("/content-studio/scoring-rule-sets")}
        />
        <QuickActionCard
          icon={GitBranch}
          iconColor="bg-indigo-500"
          title="Publish & Versions"
          description="View version history, compare diffs, audit changes, and publish content updates."
          badges={[
            { label: "Active", value: activeCount, color: "bg-green-50 text-green-700" },
            { label: "Draft", value: draftCount, color: "bg-amber-50 text-amber-700" },
            { label: "Archived", value: archivedCount },
          ]}
          onClick={() => navigate("/content-studio/versions")}
        />
      </div>

      {/* Validation Summary */}
      {validation && (
        <div className="mt-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold text-gray-900">Validation Summary</h2>
            <button
              type="button"
              onClick={() => navigate("/content-studio/validation")}
              className="text-sm font-medium text-brand-crimson hover:text-brand-crimson/80 transition-colors"
            >
              View details &rarr;
            </button>
          </div>
          <Card className="border-brand-steel/50 bg-white shadow-sm">
            <CardContent className="px-5 py-2">
              {/* Status badge */}
              <div className="flex items-center gap-2.5 mb-4">
                <div
                  className={`flex items-center justify-center w-9 h-9 rounded-lg ${
                    validation.status === "all_passed" ? "bg-brand-success" : "bg-brand-error"
                  }`}
                >
                  <ShieldCheck size={18} className="text-white" aria-hidden="true" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">
                    {validation.status === "all_passed" ? "All checks passed" : "Blocking issues found"}
                  </p>
                  <p className="text-xs text-brand-steel">
                    {validation.checks.filter((c) => c.passed).length} of {validation.checks.length} checks passed
                  </p>
                </div>
              </div>

              {/* Blocking errors */}
              {validation.blocking_errors.length > 0 && (
                <div role="alert" className="mb-4 p-3 bg-red-50 border border-brand-error/40 rounded-lg">
                  <p className="text-xs font-semibold text-brand-error mb-1">Blocking errors:</p>
                  <ul className="list-disc pl-4 space-y-0.5">
                    {validation.blocking_errors.map((err, i) => (
                      <li key={i} className="text-xs text-brand-error">
                        {err.condition}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Checks list */}
              <ul className="space-y-2">
                {validation.checks.map((check) => (
                  <li
                    key={check.name}
                    className={`flex items-center justify-between px-3 py-2.5 rounded-lg border ${
                      check.passed ? "bg-green-50 border-brand-success/30" : "bg-red-50 border-brand-error/30"
                    }`}
                  >
                    <div className="flex items-center gap-2.5">
                      {check.passed ? (
                        <Check size={16} className="text-brand-success shrink-0" aria-hidden="true" />
                      ) : (
                        <X size={16} className="text-brand-error shrink-0" aria-hidden="true" />
                      )}
                      <span className={`text-sm font-medium ${check.passed ? "text-green-800" : "text-red-800"}`}>
                        {check.name.replace(/_/g, " ")}
                      </span>
                    </div>
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-lg text-xs font-semibold ${
                        check.passed ? "bg-brand-success/10 text-brand-success" : "bg-brand-error/10 text-brand-error"
                      }`}
                    >
                      {check.passed ? "Pass" : "Fail"}
                    </span>
                  </li>
                ))}
              </ul>

            </CardContent>
          </Card>
        </div>
      )}
    </main>
  );
}

export default ContentStudioHomePage;
